import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from './user.duck';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { createImageVariantConfig } from '../util/sdkLoader';
import {
  get,
  getReceivedWishlists as getReceivedWishlistsApi,
  dismissReceivedWishlist as dismissReceivedWishlistApi,
} from '../util/api';
import { v4 as uuidv4 } from 'uuid';
import slugify from 'slugify';

// ================ Action types ================ //

export const FETCH_WISHLISTS_REQUEST = 'app/WishlistsPage/FETCH_WISHLISTS_REQUEST';
export const FETCH_WISHLISTS_SUCCESS = 'app/WishlistsPage/FETCH_WISHLISTS_SUCCESS';
export const FETCH_WISHLISTS_ERROR = 'app/WishlistsPage/FETCH_WISHLISTS_ERROR';

export const CREATE_WISHLIST_REQUEST = 'app/wishlists/CREATE_WISHLIST_REQUEST';
export const CREATE_WISHLIST_SUCCESS = 'app/wishlists/CREATE_WISHLIST_SUCCESS';
export const CREATE_WISHLIST_ERROR = 'app/wishlists/CREATE_WISHLIST_ERROR';

export const UPDATE_WISHLIST_REQUEST = 'app/wishlists/UPDATE_WISHLIST_REQUEST';
export const UPDATE_WISHLIST_SUCCESS = 'app/wishlists/UPDATE_WISHLIST_SUCCESS';
export const UPDATE_WISHLIST_ERROR = 'app/wishlists/UPDATE_WISHLIST_ERROR';

export const DELETE_WISHLIST_REQUEST = 'app/wishlists/DELETE_WISHLIST_REQUEST';
export const DELETE_WISHLIST_SUCCESS = 'app/wishlists/DELETE_WISHLIST_SUCCESS';
export const DELETE_WISHLIST_ERROR = 'app/wishlists/DELETE_WISHLIST_ERROR';

export const ADD_TO_WISHLIST_REQUEST = 'app/wishlists/ADD_TO_WISHLIST_REQUEST';
export const ADD_TO_WISHLIST_SUCCESS = 'app/wishlists/ADD_TO_WISHLIST_SUCCESS';
export const ADD_TO_WISHLIST_ERROR = 'app/wishlists/ADD_TO_WISHLIST_ERROR';

export const REMOVE_FROM_WISHLIST_REQUEST = 'app/wishlists/REMOVE_FROM_WISHLIST_REQUEST';
export const REMOVE_FROM_WISHLIST_SUCCESS = 'app/wishlists/REMOVE_FROM_WISHLIST_SUCCESS';
export const REMOVE_FROM_WISHLIST_ERROR = 'app/wishlists/REMOVE_FROM_WISHLIST_ERROR';

export const MOVE_ITEM_REQUEST = 'app/wishlists/MOVE_ITEM_REQUEST';
export const MOVE_ITEM_SUCCESS = 'app/wishlists/MOVE_ITEM_SUCCESS';
export const MOVE_ITEM_ERROR = 'app/wishlists/MOVE_ITEM_ERROR';

export const REORDER_WISHLIST_ITEMS_REQUEST = 'app/wishlists/REORDER_WISHLIST_ITEMS_REQUEST';
export const REORDER_WISHLIST_ITEMS_SUCCESS = 'app/wishlists/REORDER_WISHLIST_ITEMS_SUCCESS';
export const REORDER_WISHLIST_ITEMS_ERROR = 'app/wishlists/REORDER_WISHLIST_ITEMS_ERROR';

export const FETCH_PUBLIC_WISHLIST_REQUEST = 'app/PublicWishlistPage/FETCH_PUBLIC_WISHLIST_REQUEST';
export const FETCH_PUBLIC_WISHLIST_SUCCESS = 'app/PublicWishlistPage/FETCH_PUBLIC_WISHLIST_SUCCESS';
export const FETCH_PUBLIC_WISHLIST_ERROR = 'app/PublicWishlistPage/FETCH_PUBLIC_WISHLIST_ERROR';

export const FETCH_RECEIVED_WISHLISTS_REQUEST =
  'app/WishlistsPage/FETCH_RECEIVED_WISHLISTS_REQUEST';
export const FETCH_RECEIVED_WISHLISTS_SUCCESS =
  'app/WishlistsPage/FETCH_RECEIVED_WISHLISTS_SUCCESS';
export const FETCH_RECEIVED_WISHLISTS_ERROR = 'app/WishlistsPage/FETCH_RECEIVED_WISHLISTS_ERROR';

export const DISMISS_RECEIVED_WISHLIST_SUCCESS =
  'app/WishlistsPage/DISMISS_RECEIVED_WISHLIST_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  // Normalized wishlist data: { wishlistId: {...wishlist} }
  byId: {},
  // Array of all wishlist IDs
  allIds: [],
  // Lookup for which wishlists contain a listing: { listingId: [wishlistId1, wishlistId2] }
  byListingId: {},
  // Default wishlist ID for quick add
  defaultWishlistId: null,
  // Received wishlists (shared with me)
  receivedWishlists: [],
  fetchReceivedInProgress: false,
  fetchReceivedError: null,
  // Loading states
  fetchWishlistsInProgress: false,
  fetchWishlistsError: null,
  createWishlistInProgress: false,
  createWishlistError: null,
  updateWishlistInProgress: false,
  updateWishlistError: null,
  deleteWishlistInProgress: false,
  deleteWishlistError: null,
  addToWishlistInProgress: false,
  addToWishlistError: null,
  removeFromWishlistInProgress: false,
  removeFromWishlistError: null,
  moveItemInProgress: false,
  moveItemError: null,
  reorderItemsInProgress: false,
  reorderItemsError: null,
};

// Helper to rebuild byListingId lookup
const rebuildByListingId = wishlists => {
  const byListingId = {};
  Object.values(wishlists).forEach(wishlist => {
    wishlist.items.forEach(item => {
      if (!byListingId[item.listingId]) {
        byListingId[item.listingId] = [];
      }
      if (!byListingId[item.listingId].includes(wishlist.id)) {
        byListingId[item.listingId].push(wishlist.id);
      }
    });
  });
  return byListingId;
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_WISHLISTS_REQUEST:
      return {
        ...state,
        fetchWishlistsInProgress: true,
        fetchWishlistsError: null,
      };
    case FETCH_WISHLISTS_SUCCESS: {
      const { wishlists, defaultWishlistId } = payload;
      return {
        ...state,
        byId: wishlists,
        allIds: Object.keys(wishlists),
        byListingId: rebuildByListingId(wishlists),
        defaultWishlistId,
        fetchWishlistsInProgress: false,
      };
    }
    case FETCH_WISHLISTS_ERROR:
      return {
        ...state,
        fetchWishlistsInProgress: false,
        fetchWishlistsError: payload,
      };

    case CREATE_WISHLIST_REQUEST:
      return {
        ...state,
        createWishlistInProgress: true,
        createWishlistError: null,
      };
    case CREATE_WISHLIST_SUCCESS: {
      const newWishlist = payload;
      const updatedById = {
        ...state.byId,
        [newWishlist.id]: newWishlist,
      };
      return {
        ...state,
        byId: updatedById,
        allIds: [...state.allIds, newWishlist.id],
        byListingId: rebuildByListingId(updatedById),
        defaultWishlistId: state.defaultWishlistId || newWishlist.id,
        createWishlistInProgress: false,
      };
    }
    case CREATE_WISHLIST_ERROR:
      return {
        ...state,
        createWishlistInProgress: false,
        createWishlistError: payload,
      };

    case UPDATE_WISHLIST_REQUEST:
      return {
        ...state,
        updateWishlistInProgress: true,
        updateWishlistError: null,
      };
    case UPDATE_WISHLIST_SUCCESS: {
      const updatedWishlist = payload;
      const updatedById = {
        ...state.byId,
        [updatedWishlist.id]: updatedWishlist,
      };
      return {
        ...state,
        byId: updatedById,
        byListingId: rebuildByListingId(updatedById),
        updateWishlistInProgress: false,
      };
    }
    case UPDATE_WISHLIST_ERROR:
      return {
        ...state,
        updateWishlistInProgress: false,
        updateWishlistError: payload,
      };

    case DELETE_WISHLIST_REQUEST:
      return {
        ...state,
        deleteWishlistInProgress: true,
        deleteWishlistError: null,
      };
    case DELETE_WISHLIST_SUCCESS: {
      const wishlistId = payload;
      const { [wishlistId]: removed, ...remainingById } = state.byId;
      return {
        ...state,
        byId: remainingById,
        allIds: state.allIds.filter(id => id !== wishlistId),
        byListingId: rebuildByListingId(remainingById),
        defaultWishlistId:
          state.defaultWishlistId === wishlistId
            ? state.allIds.find(id => id !== wishlistId) || null
            : state.defaultWishlistId,
        deleteWishlistInProgress: false,
      };
    }
    case DELETE_WISHLIST_ERROR:
      return {
        ...state,
        deleteWishlistInProgress: false,
        deleteWishlistError: payload,
      };

    case ADD_TO_WISHLIST_REQUEST:
      return {
        ...state,
        addToWishlistInProgress: true,
        addToWishlistError: null,
      };
    case ADD_TO_WISHLIST_SUCCESS: {
      const { wishlistId, listingId } = payload;
      const wishlist = state.byId[wishlistId];
      if (!wishlist) return state;

      const updatedWishlist = {
        ...wishlist,
        items: [
          ...wishlist.items,
          {
            listingId,
            position: wishlist.items.length,
            addedAt: new Date().toISOString(),
          },
        ],
        updatedAt: new Date().toISOString(),
      };

      const updatedById = {
        ...state.byId,
        [wishlistId]: updatedWishlist,
      };

      return {
        ...state,
        byId: updatedById,
        byListingId: rebuildByListingId(updatedById),
        addToWishlistInProgress: false,
      };
    }
    case ADD_TO_WISHLIST_ERROR:
      return {
        ...state,
        addToWishlistInProgress: false,
        addToWishlistError: payload,
      };

    case REMOVE_FROM_WISHLIST_REQUEST:
      return {
        ...state,
        removeFromWishlistInProgress: true,
        removeFromWishlistError: null,
      };
    case REMOVE_FROM_WISHLIST_SUCCESS: {
      const { wishlistId, listingId } = payload;
      const wishlist = state.byId[wishlistId];
      if (!wishlist) return state;

      const updatedWishlist = {
        ...wishlist,
        items: wishlist.items
          .filter(item => item.listingId !== listingId)
          .map((item, index) => ({ ...item, position: index })),
        updatedAt: new Date().toISOString(),
      };

      const updatedById = {
        ...state.byId,
        [wishlistId]: updatedWishlist,
      };

      return {
        ...state,
        byId: updatedById,
        byListingId: rebuildByListingId(updatedById),
        removeFromWishlistInProgress: false,
      };
    }
    case REMOVE_FROM_WISHLIST_ERROR:
      return {
        ...state,
        removeFromWishlistInProgress: false,
        removeFromWishlistError: payload,
      };

    case MOVE_ITEM_REQUEST:
      return {
        ...state,
        moveItemInProgress: true,
        moveItemError: null,
      };
    case MOVE_ITEM_SUCCESS: {
      const { fromWishlistId, toWishlistId, listingId } = payload;

      // Remove from source wishlist
      const sourceWishlist = state.byId[fromWishlistId];
      const updatedSourceWishlist = {
        ...sourceWishlist,
        items: sourceWishlist.items
          .filter(item => item.listingId !== listingId)
          .map((item, index) => ({ ...item, position: index })),
        updatedAt: new Date().toISOString(),
      };

      // Add to target wishlist
      const targetWishlist = state.byId[toWishlistId];
      const updatedTargetWishlist = {
        ...targetWishlist,
        items: [
          ...targetWishlist.items,
          {
            listingId,
            position: targetWishlist.items.length,
            addedAt: new Date().toISOString(),
          },
        ],
        updatedAt: new Date().toISOString(),
      };

      const updatedById = {
        ...state.byId,
        [fromWishlistId]: updatedSourceWishlist,
        [toWishlistId]: updatedTargetWishlist,
      };

      return {
        ...state,
        byId: updatedById,
        byListingId: rebuildByListingId(updatedById),
        moveItemInProgress: false,
      };
    }
    case MOVE_ITEM_ERROR:
      return {
        ...state,
        moveItemInProgress: false,
        moveItemError: payload,
      };

    case REORDER_WISHLIST_ITEMS_REQUEST:
      return {
        ...state,
        reorderItemsInProgress: true,
        reorderItemsError: null,
      };
    case REORDER_WISHLIST_ITEMS_SUCCESS: {
      const { wishlistId, items } = payload;
      const wishlist = state.byId[wishlistId];
      if (!wishlist) return state;

      const updatedWishlist = {
        ...wishlist,
        items,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        byId: {
          ...state.byId,
          [wishlistId]: updatedWishlist,
        },
        reorderItemsInProgress: false,
      };
    }
    case REORDER_WISHLIST_ITEMS_ERROR:
      return {
        ...state,
        reorderItemsInProgress: false,
        reorderItemsError: payload,
      };

    case FETCH_RECEIVED_WISHLISTS_REQUEST:
      return {
        ...state,
        fetchReceivedInProgress: true,
        fetchReceivedError: null,
      };
    case FETCH_RECEIVED_WISHLISTS_SUCCESS:
      return {
        ...state,
        receivedWishlists: payload,
        fetchReceivedInProgress: false,
      };
    case FETCH_RECEIVED_WISHLISTS_ERROR:
      return {
        ...state,
        fetchReceivedInProgress: false,
        fetchReceivedError: payload,
      };
    case DISMISS_RECEIVED_WISHLIST_SUCCESS: {
      const slug = payload;
      return {
        ...state,
        receivedWishlists: state.receivedWishlists.filter(r => r.wishlistSlug !== slug),
      };
    }

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getWishlistsById = state => state.WishlistsPage.byId;
export const getAllWishlistIds = state => state.WishlistsPage.allIds;
export const getWishlistsByListingId = state => state.WishlistsPage.byListingId;
export const getDefaultWishlistId = state => state.WishlistsPage.defaultWishlistId;
export const getFetchInProgress = state => state.WishlistsPage.fetchWishlistsInProgress;
export const getFetchError = state => state.WishlistsPage.fetchWishlistsError;
export const getReceivedWishlistsList = state => state.WishlistsPage.receivedWishlists;
export const getFetchReceivedInProgress = state => state.WishlistsPage.fetchReceivedInProgress;

// Get all wishlists as an array
export const getAllWishlists = state => {
  const byId = getWishlistsById(state);
  const allIds = getAllWishlistIds(state);
  return allIds.map(id => byId[id]);
};

// Get a specific wishlist by ID
export const getWishlistById = (state, wishlistId) => {
  return getWishlistsById(state)[wishlistId] || null;
};

// Get wishlist IDs that contain a specific listing
export const getWishlistIdsForListing = (state, listingId) => {
  return getWishlistsByListingId(state)[listingId] || [];
};

// Check if a listing is in any wishlist
export const isListingInAnyWishlist = (state, listingId) => {
  return getWishlistIdsForListing(state, listingId).length > 0;
};

// Check if a listing is in a specific wishlist
export const isListingInWishlist = (state, wishlistId, listingId) => {
  const wishlist = getWishlistById(state, wishlistId);
  return wishlist ? wishlist.items.some(item => item.listingId === listingId) : false;
};

// Get public wishlists (for discovery)
export const getPublicWishlists = state => {
  return getAllWishlists(state).filter(w => w.isPublic);
};

// Helper: Get unique listings from all wishlists
export const getAllWishlistListingIds = state => {
  const wishlists = getAllWishlists(state);
  const allListingIds = new Set();
  wishlists.forEach(wishlist => {
    wishlist.items.forEach(item => {
      allListingIds.add(item.listingId);
    });
  });
  return Array.from(allListingIds);
};

// Deprecated selectors for backwards compatibility
export const getWishlists = state => {
  // For backwards compatibility with old WishlistsPage
  return getAllWishlistListingIds(state);
};

// ================ Helper Functions ================ //

// Generate unique public slug from title
export const generatePublicSlug = (title, existingSlugs = []) => {
  const baseSlug = slugify(title, { lower: true, strict: true });
  const shortId = uuidv4().slice(0, 8);
  const slug = `${baseSlug}-${shortId}`;

  // Ensure uniqueness (though UUID makes collision nearly impossible)
  if (existingSlugs.includes(slug)) {
    return generatePublicSlug(title, existingSlugs);
  }

  return slug;
};

// Migrate old array format to new object format
export const migrateWishlistsData = currentData => {
  // If already in new format (object with IDs as keys), return as is
  if (currentData && typeof currentData === 'object' && !Array.isArray(currentData)) {
    return currentData;
  }

  // If in old format (array of listing IDs), migrate
  if (Array.isArray(currentData) && currentData.length > 0) {
    const wishlistId = uuidv4();
    const now = new Date().toISOString();

    const defaultWishlist = {
      id: wishlistId,
      title: 'Meine Merkliste',
      subtitle: null,
      description: null,
      coverImageId: null,
      numberOfGuests: null,
      budget: null,
      isPublic: false,
      publicSlug: null,
      items: currentData.map((listingId, index) => ({
        listingId,
        position: index,
        addedAt: now,
      })),
      createdAt: now,
      updatedAt: now,
    };

    return {
      [wishlistId]: defaultWishlist,
    };
  }

  // Empty or null, return empty object
  return {};
};

// Extract default wishlist ID from wishlist data
export const extractDefaultWishlistId = wishlistsData => {
  if (!wishlistsData || typeof wishlistsData !== 'object') {
    return null;
  }

  const wishlistIds = Object.keys(wishlistsData);
  if (wishlistIds.length === 0) {
    return null;
  }

  // Return the first wishlist as default (could be made smarter later)
  return wishlistIds[0];
};

// ================ Action creators ================ //

export const fetchWishlistsRequest = () => ({ type: FETCH_WISHLISTS_REQUEST });
export const fetchWishlistsSuccess = (wishlists, defaultWishlistId) => ({
  type: FETCH_WISHLISTS_SUCCESS,
  payload: { wishlists, defaultWishlistId },
});
export const fetchWishlistsError = error => ({
  type: FETCH_WISHLISTS_ERROR,
  payload: error,
  error: true,
});

export const createWishlistRequest = () => ({ type: CREATE_WISHLIST_REQUEST });
export const createWishlistSuccess = wishlist => ({
  type: CREATE_WISHLIST_SUCCESS,
  payload: wishlist,
});
export const createWishlistError = error => ({
  type: CREATE_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const updateWishlistRequest = () => ({ type: UPDATE_WISHLIST_REQUEST });
export const updateWishlistSuccess = wishlist => ({
  type: UPDATE_WISHLIST_SUCCESS,
  payload: wishlist,
});
export const updateWishlistError = error => ({
  type: UPDATE_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const deleteWishlistRequest = () => ({ type: DELETE_WISHLIST_REQUEST });
export const deleteWishlistSuccess = wishlistId => ({
  type: DELETE_WISHLIST_SUCCESS,
  payload: wishlistId,
});
export const deleteWishlistError = error => ({
  type: DELETE_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const addToWishlistRequest = () => ({ type: ADD_TO_WISHLIST_REQUEST });
export const addToWishlistSuccess = (wishlistId, listingId) => ({
  type: ADD_TO_WISHLIST_SUCCESS,
  payload: { wishlistId, listingId },
});
export const addToWishlistError = error => ({
  type: ADD_TO_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const removeFromWishlistRequest = () => ({ type: REMOVE_FROM_WISHLIST_REQUEST });
export const removeFromWishlistSuccess = (wishlistId, listingId) => ({
  type: REMOVE_FROM_WISHLIST_SUCCESS,
  payload: { wishlistId, listingId },
});
export const removeFromWishlistError = error => ({
  type: REMOVE_FROM_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const moveItemRequest = () => ({ type: MOVE_ITEM_REQUEST });
export const moveItemSuccess = (fromWishlistId, toWishlistId, listingId) => ({
  type: MOVE_ITEM_SUCCESS,
  payload: { fromWishlistId, toWishlistId, listingId },
});
export const moveItemError = error => ({
  type: MOVE_ITEM_ERROR,
  payload: error,
  error: true,
});

export const reorderWishlistItemsRequest = () => ({ type: REORDER_WISHLIST_ITEMS_REQUEST });
export const reorderWishlistItemsSuccess = (wishlistId, items) => ({
  type: REORDER_WISHLIST_ITEMS_SUCCESS,
  payload: { wishlistId, items },
});
export const reorderWishlistItemsError = error => ({
  type: REORDER_WISHLIST_ITEMS_ERROR,
  payload: error,
  error: true,
});

export const fetchPublicWishlistRequest = () => ({ type: FETCH_PUBLIC_WISHLIST_REQUEST });
export const fetchPublicWishlistSuccess = wishlist => ({
  type: FETCH_PUBLIC_WISHLIST_SUCCESS,
  payload: wishlist,
});
export const fetchPublicWishlistError = error => ({
  type: FETCH_PUBLIC_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const fetchReceivedWishlistsRequest = () => ({ type: FETCH_RECEIVED_WISHLISTS_REQUEST });
export const fetchReceivedWishlistsSuccess = data => ({
  type: FETCH_RECEIVED_WISHLISTS_SUCCESS,
  payload: data,
});
export const fetchReceivedWishlistsError = error => ({
  type: FETCH_RECEIVED_WISHLISTS_ERROR,
  payload: error,
  error: true,
});
export const dismissReceivedWishlistSuccess = slug => ({
  type: DISMISS_RECEIVED_WISHLIST_SUCCESS,
  payload: slug,
});

// ================ Thunks ================ //

const storeCurrentUser = (dispatch, response) => {
  const entities = denormalisedResponseEntities(response);
  if (entities.length !== 1) {
    throw new Error('Expected a resource in the sdk.currentUser response');
  }
  dispatch(currentUserShowSuccess(entities[0]));
};

// Each write replaces the whole wishlists object: run them one at a time, each from the user as
// the API has it now, so saves from another tab or device since this page loaded aren't lost.
let pendingWishlistsWrite = Promise.resolve();
const oneAtATime = thunk => (...args) => (dispatch, getState, sdk) => {
  const result = pendingWishlistsWrite
    .then(() => sdk.currentUser.show())
    .then(response => {
      storeCurrentUser(dispatch, response);
      return thunk(...args)(dispatch, getState, sdk);
    });
  pendingWishlistsWrite = result.catch(() => {});
  return result;
};

const saveWishlists = (sdk, dispatch, wishlists) =>
  sdk.currentUser
    .updateProfile({ privateData: { wishlists } }, { expand: true })
    .then(response => storeCurrentUser(dispatch, response));

/**
 * Load user's wishlists and migrate old format if needed
 */
export const loadData = config => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchWishlistsRequest());

    const currentUser = getState().user.currentUser;
    const rawWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists;

    // Migrate old format to new format if needed
    const wishlistsData = migrateWishlistsData(rawWishlistsData);

    // If migration happened, save back to user profile
    const needsMigration = Array.isArray(rawWishlistsData);
    if (needsMigration && Object.keys(wishlistsData).length > 0) {
      sdk.currentUser
        .updateProfile({
          privateData: {
            wishlists: wishlistsData,
          },
        })
        .then(() => {
          dispatch(fetchCurrentUser());
        })
        .catch(e => {
          console.error('Failed to migrate wishlists:', e);
        });
    }

    const defaultWishlistId = extractDefaultWishlistId(wishlistsData);

    // If no wishlists, return empty success
    if (Object.keys(wishlistsData).length === 0) {
      dispatch(fetchWishlistsSuccess({}, null));
      return Promise.resolve({});
    }

    // Collect all unique listing IDs from all wishlists
    const allListingIds = new Set();
    Object.values(wishlistsData).forEach(wishlist => {
      wishlist.items.forEach(item => {
        allListingIds.add(item.listingId);
      });
    });

    if (allListingIds.size === 0) {
      dispatch(fetchWishlistsSuccess(wishlistsData, defaultWishlistId));
      return Promise.resolve(wishlistsData);
    }

    // Fetch listing data for all listings in all wishlists
    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
      config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const params = {
      ids: Array.from(allListingIds),
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.categoryLevel2',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // Listing badges: 'new' is computed from these, 'popular' is written by the metrics job.
        'createdAt',
        'metadata.badges',
        'metadata.publishedAt',
        // The card's stars, written by server/jobs/reviewStats.js.
        'metadata.reviewStats',
      ],
      // profile.publicData carries the provider's awards, shown as the 'Ausgezeichnet' badge.
      'fields.user': ['profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData',
        'profile.metadata',],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-300`,
        `variants.${variantPrefix}-600`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-300`, 300, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-600`, 600, aspectRatio),
      // Deliberately no 'limit.images': these listings go into the shared entity store, and a
      // truncated images relationship replaces the full one a listing page already fetched -
      // which would cut that page's gallery down to a single image.
    };

    return sdk.listings
      .query(params)
      .then(response => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        // Add listings to marketplace data (for denormalization)
        dispatch(addMarketplaceEntities(response, sanitizeConfig));

        // A save during the query triggers a newer load that can finish first, so store the
        // wishlists as they are now rather than as this load found them.
        const latest = getState().user.currentUser?.attributes?.profile?.privateData?.wishlists;
        const latestWishlists = latest && !Array.isArray(latest) ? latest : wishlistsData;
        dispatch(fetchWishlistsSuccess(latestWishlists, extractDefaultWishlistId(latestWishlists)));
        return response;
      })
      .catch(e => {
        console.error('Error loading wishlists:', e);
        dispatch(fetchWishlistsError(storableError(e)));
        throw e;
      });
  };
};

/**
 * Create a new wishlist
 */
export const createWishlist = oneAtATime(wishlistData => {
  return (dispatch, getState, sdk) => {
    dispatch(createWishlistRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const now = new Date().toISOString();
    const wishlistId = uuidv4();

    // Generate public slug if wishlist is public
    const existingSlugs = Object.values(currentWishlistsData)
      .filter(w => w.publicSlug)
      .map(w => w.publicSlug);

    const publicSlug = wishlistData.isPublic
      ? generatePublicSlug(wishlistData.title, existingSlugs)
      : null;

    const newWishlist = {
      id: wishlistId,
      title: wishlistData.title || 'Neue Merkliste',
      subtitle: wishlistData.subtitle || null,
      description: wishlistData.description || null,
      coverImageUrl: wishlistData.coverImageUrl || null,
      coverImageThumbnails: wishlistData.coverImageThumbnails || null,
      numberOfGuests: wishlistData.numberOfGuests || null,
      budget: wishlistData.budget || null,
      isPublic: wishlistData.isPublic || false,
      publicSlug,
      items: [],
      createdAt: now,
      updatedAt: now,
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [wishlistId]: newWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(createWishlistSuccess(newWishlist));
        return newWishlist;
      })
      .catch(e => {
        dispatch(createWishlistError(storableError(e)));
        console.error('Error creating wishlist:', e);
        throw e;
      });
  };
});

/**
 * Update an existing wishlist
 */
export const updateWishlist = oneAtATime((wishlistId, updates) => {
  return (dispatch, getState, sdk) => {
    dispatch(updateWishlistRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const existingWishlist = currentWishlistsData[wishlistId];
    if (!existingWishlist) {
      const error = new Error('Wishlist not found');
      dispatch(updateWishlistError(storableError(error)));
      return Promise.reject(error);
    }

    // Handle public slug generation if changing to public or ensuring slug for sharing
    let publicSlug = existingWishlist.publicSlug;
    if (updates.isPublic && !existingWishlist.publicSlug) {
      // Creating new public slug
      const existingSlugs = Object.values(currentWishlistsData)
        .filter(w => w.publicSlug && w.id !== wishlistId)
        .map(w => w.publicSlug);
      publicSlug = generatePublicSlug(updates.title || existingWishlist.title, existingSlugs);
    } else if (updates.ensureSlug && !publicSlug) {
      // Generate slug for sharing without making public
      const existingSlugs = Object.values(currentWishlistsData)
        .filter(w => w.publicSlug && w.id !== wishlistId)
        .map(w => w.publicSlug);
      publicSlug = generatePublicSlug(existingWishlist.title, existingSlugs);
    }

    // Remove ensureSlug from the updates before merging — it's not a wishlist field
    const { ensureSlug, ...cleanUpdates } = updates;

    const updatedWishlist = {
      ...existingWishlist,
      ...cleanUpdates,
      publicSlug,
      id: wishlistId, // Ensure ID doesn't change
      items: existingWishlist.items, // Don't allow updating items through this method
      updatedAt: new Date().toISOString(),
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [wishlistId]: updatedWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(updateWishlistSuccess(updatedWishlist));
        return updatedWishlist;
      })
      .catch(e => {
        dispatch(updateWishlistError(storableError(e)));
        console.error('Error updating wishlist:', e);
        throw e;
      });
  };
});

/**
 * Delete a wishlist
 */
export const deleteWishlist = oneAtATime(wishlistId => {
  return (dispatch, getState, sdk) => {
    dispatch(deleteWishlistRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    if (!currentWishlistsData[wishlistId]) {
      const error = new Error('Wishlist not found');
      dispatch(deleteWishlistError(storableError(error)));
      return Promise.reject(error);
    }

    const { [wishlistId]: removed, ...remainingWishlists } = currentWishlistsData;

    return saveWishlists(sdk, dispatch, remainingWishlists)
      .then(() => {
        dispatch(deleteWishlistSuccess(wishlistId));
        return wishlistId;
      })
      .catch(e => {
        dispatch(deleteWishlistError(storableError(e)));
        console.error('Error deleting wishlist:', e);
        throw e;
      });
  };
});

/**
 * Add a listing to a wishlist
 */
export const addToWishlist = oneAtATime((wishlistId, listingId) => {
  return (dispatch, getState, sdk) => {
    dispatch(addToWishlistRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const wishlist = currentWishlistsData[wishlistId];
    if (!wishlist) {
      const error = new Error('Wishlist not found');
      dispatch(addToWishlistError(storableError(error)));
      return Promise.reject(error);
    }

    // Check if listing already in wishlist
    const alreadyExists = wishlist.items.some(item => item.listingId === listingId);
    if (alreadyExists) {
      // Already in wishlist, just dispatch success
      dispatch(addToWishlistSuccess(wishlistId, listingId));
      return Promise.resolve();
    }

    const updatedWishlist = {
      ...wishlist,
      items: [
        ...wishlist.items,
        {
          listingId,
          position: wishlist.items.length,
          addedAt: new Date().toISOString(),
        },
      ],
      updatedAt: new Date().toISOString(),
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [wishlistId]: updatedWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(addToWishlistSuccess(wishlistId, listingId));
        return { wishlistId, listingId };
      })
      .catch(e => {
        dispatch(addToWishlistError(storableError(e)));
        console.error('Error adding to wishlist:', e);
        throw e;
      });
  };
});

/**
 * Remove a listing from a wishlist
 */
export const removeFromWishlist = oneAtATime((wishlistId, listingId) => {
  return (dispatch, getState, sdk) => {
    dispatch(removeFromWishlistRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const wishlist = currentWishlistsData[wishlistId];
    if (!wishlist) {
      const error = new Error('Wishlist not found');
      dispatch(removeFromWishlistError(storableError(error)));
      return Promise.reject(error);
    }

    const updatedWishlist = {
      ...wishlist,
      items: wishlist.items
        .filter(item => item.listingId !== listingId)
        .map((item, index) => ({ ...item, position: index })),
      updatedAt: new Date().toISOString(),
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [wishlistId]: updatedWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(removeFromWishlistSuccess(wishlistId, listingId));
        return { wishlistId, listingId };
      })
      .catch(e => {
        dispatch(removeFromWishlistError(storableError(e)));
        console.error('Error removing from wishlist:', e);
        throw e;
      });
  };
});

/**
 * Move an item from one wishlist to another
 */
export const moveItem = oneAtATime((fromWishlistId, toWishlistId, listingId) => {
  return (dispatch, getState, sdk) => {
    dispatch(moveItemRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const sourceWishlist = currentWishlistsData[fromWishlistId];
    const targetWishlist = currentWishlistsData[toWishlistId];

    if (!sourceWishlist) {
      const error = new Error('Source wishlist not found');
      dispatch(moveItemError(storableError(error)));
      return Promise.reject(error);
    }

    if (!targetWishlist) {
      const error = new Error('Target wishlist not found');
      dispatch(moveItemError(storableError(error)));
      return Promise.reject(error);
    }

    // Check if item is in source wishlist
    const itemIndex = sourceWishlist.items.findIndex(item => item.listingId === listingId);
    if (itemIndex === -1) {
      const error = new Error('Item not found in source wishlist');
      dispatch(moveItemError(storableError(error)));
      return Promise.reject(error);
    }

    // Check if item is already in target wishlist
    const alreadyInTarget = targetWishlist.items.some(item => item.listingId === listingId);
    if (alreadyInTarget) {
      const error = new Error('Item already exists in target wishlist');
      dispatch(moveItemError(storableError(error)));
      return Promise.reject(error);
    }

    // Remove from source
    const updatedSourceWishlist = {
      ...sourceWishlist,
      items: sourceWishlist.items
        .filter(item => item.listingId !== listingId)
        .map((item, index) => ({ ...item, position: index })),
      updatedAt: new Date().toISOString(),
    };

    // Add to target
    const updatedTargetWishlist = {
      ...targetWishlist,
      items: [
        ...targetWishlist.items,
        {
          listingId,
          position: targetWishlist.items.length,
          addedAt: new Date().toISOString(),
        },
      ],
      updatedAt: new Date().toISOString(),
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [fromWishlistId]: updatedSourceWishlist,
      [toWishlistId]: updatedTargetWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(moveItemSuccess(fromWishlistId, toWishlistId, listingId));
        return { fromWishlistId, toWishlistId, listingId };
      })
      .catch(e => {
        dispatch(moveItemError(storableError(e)));
        console.error('Error moving item between wishlists:', e);
        throw e;
      });
  };
});

/**
 * Reorder items within a wishlist
 */
export const reorderWishlistItems = oneAtATime((wishlistId, newItems) => {
  return (dispatch, getState, sdk) => {
    dispatch(reorderWishlistItemsRequest());

    const currentUser = getState().user.currentUser;
    const currentWishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists || {};

    const wishlist = currentWishlistsData[wishlistId];
    if (!wishlist) {
      const error = new Error('Wishlist not found');
      dispatch(reorderWishlistItemsError(storableError(error)));
      return Promise.reject(error);
    }

    const updatedWishlist = {
      ...wishlist,
      items: newItems.map((item, index) => ({ ...item, position: index })),
      updatedAt: new Date().toISOString(),
    };

    const updatedWishlists = {
      ...currentWishlistsData,
      [wishlistId]: updatedWishlist,
    };

    return saveWishlists(sdk, dispatch, updatedWishlists)
      .then(() => {
        dispatch(reorderWishlistItemsSuccess(wishlistId, updatedWishlist.items));
        return updatedWishlist.items;
      })
      .catch(e => {
        dispatch(reorderWishlistItemsError(storableError(e)));
        console.error('Error reordering wishlist items:', e);
        throw e;
      });
  };
});

// Backwards compatibility: Toggle wishlist (now adds to default wishlist)
export const toggleWishlist = (listingId, isInWishlist) => {
  return (dispatch, getState, sdk) => {
    const state = getState();
    const defaultWishlistId = getDefaultWishlistId(state);

    if (!defaultWishlistId) {
      // No default wishlist, create one first
      return dispatch(createWishlist({ title: 'Meine Merkliste' })).then(newWishlist => {
        return dispatch(addToWishlist(newWishlist.id, listingId));
      });
    }

    if (isInWishlist) {
      return dispatch(removeFromWishlist(defaultWishlistId, listingId));
    } else {
      return dispatch(addToWishlist(defaultWishlistId, listingId));
    }
  };
};

/**
 * Load a public wishlist by its slug
 * This searches through all users to find a wishlist with matching publicSlug
 */
export const loadPublicWishlistBySlug = (slug, config) => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchPublicWishlistRequest());

    return get(`/api/wishlists/public/${slug}`)
      .then(response => {
        const wishlist = response?.data;
        if (!wishlist) {
          const error = new Error('Wishlist not found');
          dispatch(fetchPublicWishlistError(storableError(error)));
          throw error;
        }

        // Fetch listing data for items in the wishlist
        if (!Array.isArray(wishlist.items) || wishlist.items.length === 0) {
          dispatch(fetchPublicWishlistSuccess(wishlist));
          return wishlist;
        }

        const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
          config?.layout?.listingImage || {};
        const aspectRatio = aspectHeight / aspectWidth;

        const listingIds = wishlist.items.map(item => item.listingId);
        const params = {
          ids: listingIds,
          include: ['author', 'images'],
          'fields.listing': [
            'title',
            'geolocation',
            'price',
            'deleted',
            'state',
            'publicData.listingType',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            // Listing badges: 'new' is computed from these, 'popular' is written by the metrics job.
            'createdAt',
            'metadata.badges',
            'metadata.publishedAt',
            // The card's stars, written by server/jobs/reviewStats.js.
            'metadata.reviewStats',
          ],
          // profile.publicData carries the provider's awards, shown as the 'Ausgezeichnet' badge.
          'fields.user': ['profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData',
        'profile.metadata',],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
            `variants.${variantPrefix}-300`,
            `variants.${variantPrefix}-600`,
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-300`, 300, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-600`, 600, aspectRatio),
          // See loadData: a truncated images relationship would overwrite a fuller one.
        };

        return sdk.listings.query(params).then(listingsResponse => {
          const listingFields = config?.listing?.listingFields;
          const sanitizeConfig = { listingFields };

          // Add listings to marketplace data
          dispatch(addMarketplaceEntities(listingsResponse, sanitizeConfig));

          // Return wishlist data
          dispatch(fetchPublicWishlistSuccess(wishlist));
          return wishlist;
        });
      })
      .catch(e => {
        dispatch(fetchPublicWishlistError(storableError(e)));
        console.error('Error loading public wishlist:', e);
        throw e;
      });
  };
};

/**
 * Fetch wishlists that others have shared with the current user
 */
export const loadReceivedWishlists = () => {
  return dispatch => {
    dispatch(fetchReceivedWishlistsRequest());

    return getReceivedWishlistsApi()
      .then(response => {
        const data = response?.data || [];
        dispatch(fetchReceivedWishlistsSuccess(data));
        return data;
      })
      .catch(e => {
        dispatch(fetchReceivedWishlistsError(storableError(e)));
      });
  };
};

/**
 * Dismiss (remove) a received wishlist from the user's list
 */
export const dismissReceived = slug => {
  return dispatch => {
    return dismissReceivedWishlistApi(slug)
      .then(() => {
        dispatch(dismissReceivedWishlistSuccess(slug));
      })
      .catch(e => {
        console.error('Failed to dismiss received wishlist:', e);
      });
  };
};
